/**
 * TODO: rewrite with state hooks
 */
import * as React from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { Dispatch } from "redux";

import { debounce } from "lodash-es";

import * as analytics from "../../analytics";
import { SearchAddress } from "../../components/molecules";
import {
  BookingLocationPresenter,
  inflateBookingLocationPresenter,
} from "../../presenters/booking-location";
import {
  checkSupportedPostcode,
  getSuggestedAddresses,
  IAddressAutocomplete,
  IAddressSuggestion,
} from "../../services/api/postcodes";
import { IRootState } from "../../store/index";
import {
  actionCreators as postcodeActions,
  fetchAddressUdprnData,
  findPostcodeSuggestions,
  getAddressSuggestionsData,
  selectPostcodeArea,
} from "../../store/postcodes/actions";
import { actionCreators as therapistsActions } from "../../store/therapists/actions";
import { config } from "../../config";

interface IProps {
  onClick(): void;
  onChange?(value: string): void;
  onSelect?(): void;
  trackChange(value: string | undefined): void;
}

interface IReduxProps {
  bookingLocation?: BookingLocationPresenter;
  isPostalAddressLoading: boolean;
  isSuggestedAddressesLoaded: boolean;
  isSuggestedAddressesLoading: boolean;
  selectedSuggestedAddress?: string;
  suggestedEntry?: string;
}

interface IState {
  addressSuggestions: (string | IAddressSuggestion)[];
  addressValue: string;
  showInstructionalInitialMessage: boolean;
  showInstructionalMessage: boolean;
  showNoResults: boolean;
  showUnSupportedPostcodeModal: boolean;
  suggestedPostcodes: string[];
  suggestionsAddress: IAddressAutocomplete[];
}

type RouteProps = RouteComponentProps<{ categoryName: string }>;
type Props = IProps & IReduxProps & RouteProps & { dispatch: Dispatch<any> };

class SearchAddressContainer extends React.Component<Props, IState> {
  private readonly debounceTime = 300;
  private readonly minLength = 5;

  public state: IState = {
    addressSuggestions: [],
    addressValue: this.props.selectedSuggestedAddress || ("" as string),
    showInstructionalInitialMessage: false,
    showInstructionalMessage: false,
    showNoResults: false,
    showUnSupportedPostcodeModal: false,
    suggestedPostcodes: [],
    suggestionsAddress: [],
  };

  public componentDidMount(): void {
    this.props.dispatch(postcodeActions.clearPostcodeSuggestions());
    this.props.dispatch(postcodeActions.clearAddressSuggestions());
  }

  // Workaround with state update to skip debounce for postcodes
  public debounceGetSuggestions = debounce(
    (address: string) => this.getSuggestions(address),
    this.state.addressValue.length >= this.minLength
      ? this.debounceTime / 2
      : this.debounceTime,
  );

  /**
   * Get value from input and set the state
   */
  public updateValue = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const addressValue = e.currentTarget.value;
    this.setState({ addressValue }, async () => {
      await this.debounceGetSuggestions(addressValue);
    });

    if (this.props.onChange) {
      this.props.onChange(addressValue);
    }
  };

  /**
   * Dispatch action to get suggestions list of addresses / postcode areas
   */
  public async getSuggestions(address: string) {
    const valueCheck = address.replace(/\s/g, "");

    if (address.length < this.minLength && valueCheck.length > 0) {
      // workaround with state update to update postcodes suggestions
      await this.props.dispatch(
        findPostcodeSuggestions(valueCheck.toUpperCase()),
      );

      const postcodes = config.postcodeAreas;
      if (typeof postcodes === "undefined") {
        // Postcodes functionality is disabled, or misconfigured
        return;
      }
      const matches = postcodes.filter((s) =>
        s.match(`^${valueCheck.toUpperCase()}`),
      );

      this.setState({
        suggestedPostcodes: matches,
        addressSuggestions: matches,
      });
    }

    if (
      address.length >= this.minLength &&
      valueCheck.length >= this.minLength
    ) {
      // workaround with state update to update addresses suggestions
      this.props.dispatch(postcodeActions.setSuggestedEntry(address));

      const data = await getSuggestedAddresses(address);
      await this.props.dispatch(getAddressSuggestionsData(data, address));

      this.setState({
        addressSuggestions: [...data, ...this.state.suggestedPostcodes],
        suggestionsAddress: data,
      });
    }

    if (!address.length) {
      this.props.dispatch(postcodeActions.resetSuggestedState());
      this.setState({
        addressSuggestions: [],
        suggestionsAddress: [],
        suggestedPostcodes: [],
        showInstructionalInitialMessage: true,
        showInstructionalMessage: false,
      });
    } else {
      const showInstructionalMessage =
        this.state.addressValue.length > 0 &&
        this.state.addressValue.length < this.minLength &&
        !this.state.suggestionsAddress.length &&
        this.state.suggestedPostcodes.length > 0;

      const showNoResults = Boolean(
        this.props.isSuggestedAddressesLoaded &&
          !this.state.addressSuggestions.length &&
          address.length,
      );

      this.setState({
        showInstructionalInitialMessage: false,
        showInstructionalMessage,
        showNoResults,
      });
    }
  }

  /**
   * Select suggestion
   */
  public selectValue = (address: IAddressSuggestion | string) => {
    this.props.dispatch(therapistsActions.clearDate());

    if (typeof address === "object") {
      const udprn = address.udprn;
      const addressValue = address.suggestion;

      this.setState({ addressValue });
      this.setAddress(udprn, addressValue);
    } else {
      this.setState({ addressValue: address }, async () => {
        // tslint:disable: await-promise
        await this.props.dispatch(postcodeActions.setSuggestedAddress(address));
        await this.props.dispatch(selectPostcodeArea(address));
        // tslint:enable: await-promise

        if (this.props.onSelect) {
          this.props.onSelect();
        }
      });
    }
  };

  /**
   * Set Full Postal Address in store
   */
  public setAddress = async (udprn: number, addressValue: string) => {
    if (
      udprn === undefined &&
      this.props.selectedSuggestedAddress !== addressValue
    ) {
      return;
    }

    if (addressValue !== this.props.selectedSuggestedAddress) {
      // tslint:disable: await-promise
      await this.props.dispatch(fetchAddressUdprnData(udprn));
      await this.props.dispatch(
        postcodeActions.setSuggestedAddress(addressValue),
      );
      // tslint:enable: await-promise

      const postcode = this.props.bookingLocation.getPostCodeOutwardCode();
      const isSupported = await checkSupportedPostcode(postcode);

      if (!isSupported) {
        this.setState({ showUnSupportedPostcodeModal: true });
      }

      if (this.props.onSelect) {
        this.props.onSelect();
      }

      analytics.track({
        name: analytics.AnalyticsEvents.ADDRESS_SELECT,
        payload: {
          addressValue,
        },
      });
    }
  };

  public closeModal = () => {
    this.setState({ showUnSupportedPostcodeModal: false });
  };

  public checkToShowInitialMessage = () => {
    const showInstructionalInitialMessage =
      !this.state.addressValue.length &&
      !this.state.suggestionsAddress.length &&
      !this.state.suggestedPostcodes.length;

    this.props.onClick();

    this.setState({ showInstructionalInitialMessage });
  };

  /**
   * Make preselection of area code or address on input blur
   * when correct suggestion exists
   * If there is no suggestions - make empty
   * Prevents confusion if area or address selected or not
   */
  public preselectTypedAddress = () => {
    // do not run if no changes
    if (this.state.addressValue === this.props.selectedSuggestedAddress) {
      return;
    }

    const addressValueAreaCode = this.state.addressValue.toUpperCase();

    const areaCodePreselection =
      this.state.addressValue.length > 0 &&
      this.state.addressValue.length < this.minLength &&
      this.state.addressSuggestions.indexOf(addressValueAreaCode.trim()) > -1;

    const wrongAreaCode =
      this.state.addressValue.length > 0 &&
      this.state.addressValue.length < this.minLength &&
      this.state.addressSuggestions.indexOf(addressValueAreaCode.trim()) === -1;

    const addressPreselection =
      this.state.addressValue.length >= this.minLength &&
      this.props.selectedSuggestedAddress &&
      this.props.selectedSuggestedAddress !== this.state.addressValue;

    if (areaCodePreselection) {
      this.selectValue(addressValueAreaCode);
      this.props.dispatch(
        postcodeActions.setSuggestedAddress(addressValueAreaCode),
      );
    } else if (addressPreselection) {
      this.setState({ addressValue: this.props.selectedSuggestedAddress });
    } else if (
      (this.state.addressValue.length === 0 &&
        this.props.selectedSuggestedAddress) ||
      wrongAreaCode
    ) {
      this.props.dispatch(postcodeActions.clearAddress());
      this.setState({ addressValue: "" });
    }
  };

  /**
   * Render search address component
   */
  public render() {
    return (
      <SearchAddress
        isItemsLoading={this.props.isSuggestedAddressesLoading}
        items={this.state.addressSuggestions}
        selectedItem={this.state.addressValue}
        onChange={this.updateValue}
        onSelect={this.selectValue}
        onClick={this.checkToShowInitialMessage}
        onClose={this.preselectTypedAddress}
        onCloseModal={this.closeModal}
        showInstructionalInitialMessage={
          this.state.showInstructionalInitialMessage
        }
        showInstructionalMessage={this.state.showInstructionalMessage}
        showNoResults={this.state.showNoResults}
        showUnSupportedPostcodeModal={this.state.showUnSupportedPostcodeModal}
        trackChange={this.props.trackChange}
      />
    );
  }
}

// store
const mapStateToProps = (state: IRootState): IReduxProps => {
  return {
    ...(state.postcodesState.bookingLocation && {
      bookingLocation: inflateBookingLocationPresenter(
        state.postcodesState.bookingLocation,
      ),
    }),
    isPostalAddressLoading: state.postcodesState.isPostalAddressLoading,
    isSuggestedAddressesLoaded: state.postcodesState.isSuggestedAddressesLoaded,
    isSuggestedAddressesLoading:
      state.postcodesState.isSuggestedAddressesLoading,
    selectedSuggestedAddress: state.postcodesState.selectedSuggestedAddress,
    suggestedEntry: state.postcodesState.suggestedEntry,
  };
};

const store = withRouter(
  connect<IReduxProps, IState, {}, IRootState>(mapStateToProps)(
    SearchAddressContainer,
  ),
);

export { store as SearchAddressContainer };
